const normalizeChangeHistoryCacheValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeChangeHistoryCacheValue)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeChangeHistoryCacheValue(value[key])
        return accumulator
      }, {})
  }

  return value
}

export const buildChangeHistoryCacheKey = (queryParams = {}) => {
  const { page_number, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeChangeHistoryCacheValue(baseQueryParams))
}

// Backend column sorting (gated by SF_FEATURE_FLAG_BACKEND_COLUMN_SORTING).
// Server-side sorting is backed by HISTORY_VERSION_SORT_WHITELIST in
// history_versions_sort_helper.rb. Computed display columns such as Change
// Location, Change Action, and Change Details are not sortable.
export const CHANGE_HISTORY_DEFAULT_SORT_COLUMN = 'logdate'
export const CHANGE_HISTORY_DEFAULT_SORT_DIRECTION = 'desc'
export const CHANGE_HISTORY_BACKEND_SORTABLE_COLUMNS = ['logdate', 'campaign', 'brand_name', 'account', 'username', 'admin_username', 'client_username']

export const isChangeHistoryColumnBackendSortable = (dataField) =>
  CHANGE_HISTORY_BACKEND_SORTABLE_COLUMNS.includes(dataField)

// Mutates and returns queryParams with the validated sort_column/sort_direction
// the backend expects. Unsupported columns fall back to the default so the API
// receives a whitelisted value.
export const applyChangeHistorySortParams = (queryParams, sortState) => {
  queryParams.sort_column = isChangeHistoryColumnBackendSortable(sortState?.column)
    ? sortState.column
    : CHANGE_HISTORY_DEFAULT_SORT_COLUMN
  queryParams.sort_direction = sortState?.direction || CHANGE_HISTORY_DEFAULT_SORT_DIRECTION
  return queryParams
}

const getUniqueChangeHistoryRows = (rows = []) => {
  const uniqueRows = []

  rows.forEach((item) => {
    const existingIndex = uniqueRows.findIndex((existingItem) => existingItem.id == item.id)

    if (existingIndex <= -1) {
      uniqueRows.push(item)
    }
  })

  return uniqueRows
}

export const handleGetChangeHistory = (state, action, config) => {
  const { dataKey } = config
  const queryParams = action.payload?.queryParams || {}
  const pageNumber = parseInt(queryParams.page_number, 10) || 1
  const useCache = Boolean(action.payload?.useCache)
  const appendResults = Boolean(action.payload?.appendResults)
  const cacheKey = useCache ? buildChangeHistoryCacheKey(queryParams) : null
  const shouldResetCache =
    useCache &&
    pageNumber === 1 &&
    !appendResults &&
    state.active_cache_key &&
    state.active_cache_key !== cacheKey
  const cachedPage = useCache ? state.page_cache?.[cacheKey]?.[pageNumber] : null

  return {
    ...state,
    loading: !cachedPage,
    error: null,
    active_cache_key: useCache ? cacheKey : null,
    [dataKey]: cachedPage?.rows || ((pageNumber === 1 || !appendResults) ? [] : state[dataKey]),
    last_page_fetched: cachedPage?.page_number || state.last_page_fetched,
    total_number_of_pages: cachedPage?.total_number_of_pages || state.total_number_of_pages,
    has_next_page: typeof cachedPage?.has_next_page === 'boolean' ? cachedPage.has_next_page : state.has_next_page,
    has_lookahead_pages: typeof cachedPage?.has_lookahead_pages === 'boolean'
      ? cachedPage.has_lookahead_pages
      : state.has_lookahead_pages,
    page_cache: shouldResetCache ? {} : state.page_cache,
  }
}

export const handleGetChangeHistorySuccess = (state, action, config) => {
  const { dataKey, additionalDataMap = {} } = config

  if (action.payload instanceof Array) {
    return {
      ...state,
      [dataKey]: action.payload,
      loading: false,
      error: null,
    }
  }

  const rows = Array.isArray(action.payload?.data) ? action.payload.data : []
  const additional = action.payload?.additional_data || {}
  const queryParams = action.meta?.queryParams || {}
  const appendResults = Boolean(action.meta?.appendResults)
  const useCache = Boolean(action.meta?.useCache)
  const pageNumber = parseInt(additional.page_number, 10)
  const totalPages = parseInt(additional.total_number_of_pages, 10)
  const safePage = Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1
  const safeTotal = Number.isFinite(totalPages) && totalPages >= 0 ? totalPages : 0
  const hasNextPage = typeof additional.has_next_page === 'boolean' ? additional.has_next_page : false
  const hasLookAheadPages = typeof additional.has_lookahead_pages === 'boolean' ? additional.has_lookahead_pages : false
  const cacheKey = useCache ? buildChangeHistoryCacheKey(queryParams) : null
  const pageSnapshot = {
    rows,
    page_number: safePage,
    total_number_of_pages: safeTotal,
    has_next_page: hasNextPage,
    has_lookahead_pages: hasLookAheadPages,
  }

  const mappedAdditionalState = Object.entries(additionalDataMap).reduce((accumulator, [stateKey, payloadKey]) => {
    accumulator[stateKey] = additional?.[payloadKey] ?? state[stateKey]
    return accumulator
  }, {})

  return {
    ...state,
    ...mappedAdditionalState,
    [dataKey]: appendResults
      ? getUniqueChangeHistoryRows([...state[dataKey], ...rows])
      : rows,
    last_start_date: additional.start_date,
    last_end_date: additional.end_date,
    last_page_fetched: safePage,
    total_number_of_pages: safeTotal,
    has_next_page: hasNextPage,
    has_lookahead_pages: hasLookAheadPages,
    loading: false,
    error: null,
    active_cache_key: useCache ? cacheKey : state.active_cache_key,
    last_fetched_at: new Date(),
    page_cache: useCache ? {
      ...state.page_cache,
      [cacheKey]: {
        ...(state.page_cache?.[cacheKey] || {}),
        [safePage]: pageSnapshot,
      },
    } : state.page_cache,
  }
}

export const handleGetChangeHistoryError = (state, action) => ({
  ...state,
  error: action.payload,
  loading: false,
  has_next_page: false,
})
